import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Tabs,
  Tab,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Edit as EditIcon } from '@mui/icons-material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CopyToClipboardText from '../ui-components/CopyToClipboardText';




const BrandsOperatorsPage = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [brands, setBrands] = useState([]);
  const [operators, setOperators] = useState([]);
  const [totalShare, setTotalShare] = useState(0);

  // Dialog states
  const [createBrandDialogOpen, setCreateBrandDialogOpen] = useState(false);
  const [createOperatorDialogOpen, setCreateOperatorDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // "brand" or "operator"

  // Create Brand states
  const [newBrandName, setNewBrandName] = useState('');
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [newBrandShare, setNewBrandShare] = useState('');

  // Create Operator states
  const [newOperatorName, setNewOperatorName] = useState('');
  const [newLicenseNumber, setNewLicenseNumber] = useState('');

  // Edit Brand states
  const [editBrandDialogOpen, setEditBrandDialogOpen] = useState(false);
  const [brandToEdit, setBrandToEdit] = useState(null);
  const [updatedBrandName, setUpdatedBrandName] = useState('');
  const [updatedOperator, setUpdatedOperator] = useState(null);
  const [updatedShare, setUpdatedShare] = useState('');

  // Extract unique options for dropdowns and allow region and country setting
  const countryOptions = [...new Set(brands.map(b => b.Metadata?.Country).filter(Boolean))];
  const regionOptions = [...new Set(brands.map(b => b.Metadata?.Region).filter(Boolean))];

  // Sort options for the table
  const [sortField, setSortField] = useState('BrandName');
  const [sortDirection, setSortDirection] = useState('asc');

  const [updatedBrandCountry, setUpdatedBrandCountry] = useState('');
  const [updatedBrandRegion, setUpdatedBrandRegion] = useState('');

  // Create Brand
  const [newBrandCountry, setNewBrandCountry] = useState('');
  const [newBrandRegion, setNewBrandRegion] = useState('');





  const sortedBrands = [...brands].sort((a, b) => {
    const aValue = (a[sortField] || a.Metadata?.[sortField] || '').toString().toLowerCase();
    const bValue = (b[sortField] || b.Metadata?.[sortField] || '').toString().toLowerCase();
    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />;
  };


  useEffect(() => {
    fetchBrandsAndOperators();
  }, []);


  const [selectedCountry, setSelectedCountry] = useState('All Countries');

  const filteredBrands = selectedCountry === 'All Countries'
    ? sortedBrands
    : sortedBrands.filter(b => b.Metadata?.Country === selectedCountry);

  const shareByRegionAndCountry = React.useMemo(() => {
    const regionMap = {};

    brands.forEach((brand) => {
      const share = parseFloat(brand.Share || 0) * 100;
      const region = brand.Metadata?.Region || 'Unknown Region';
      const country = brand.Metadata?.Country || 'Unknown Country';

      if (!regionMap[region]) {
        regionMap[region] = {};
      }
      if (!regionMap[region][country]) {
        regionMap[region][country] = 0;
      }

      regionMap[region][country] += share;
    });

    return regionMap;
  }, [brands]);




  const fetchBrandsAndOperators = async () => {
    try {
      const brandsResponse = await axios.get(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/brands`, {
        headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
      });
      setBrands(brandsResponse.data.brands || []);

      const operatorsResponse = await axios.get(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/operators`, {
        headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
      });
      setOperators(operatorsResponse.data.operators || []);

      calculateTotalShare(brandsResponse.data.brands || []);
    } catch (error) {
      console.error('Error fetching brands or operators:', error);
    }
  };

  const calculateTotalShare = (brandsList) => {
    const total = brandsList.reduce((sum, brand) => sum + parseFloat(brand.Share || 0), 0) * 100;
    setTotalShare(total.toFixed(1));
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleCreateBrand = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/brands`,
        {
          BrandName: newBrandName,
          OperatorID: selectedOperator.OperatorID,
          Share: parseFloat(newBrandShare) / 100,
          Metadata: {
            Country: newBrandCountry,
            Region: newBrandRegion
          }
        },
        { headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY } }
      );
      setCreateBrandDialogOpen(false);
      fetchBrandsAndOperators();
    } catch (error) {
      console.error('Error creating brand:', error);
    }
  };

  const handleCreateOperator = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/operators`,
        {
          OperatorName: newOperatorName,
          LicenseNumber: newLicenseNumber,
        },
        { headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY } }
      );
      setCreateOperatorDialogOpen(false);
      fetchBrandsAndOperators();
    } catch (error) {
      console.error('Error creating operator:', error);
    }
  };

  const handleDeleteItem = async () => {
    try {
      const url = deleteType === 'brand' ? '/brands' : '/operators';
      await axios.delete(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}${url}`, {
        headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
        data: { [`${deleteType === 'brand' ? 'BrandID' : 'OperatorID'}`]: itemToDelete },
      });
      setDeleteDialogOpen(false);
      fetchBrandsAndOperators();
    } catch (error) {
      console.error(`Error deleting ${deleteType}:`, error);
    }
  };

  const confirmDelete = (id, type) => {
    // If attempting to delete an operator, check if any brands are associated with it
    if (type === 'operator') {
      const associatedBrands = brands.filter((brand) => brand.OperatorID === id);
      if (associatedBrands.length > 0) {
        alert(
          `Cannot delete operator. There are ${associatedBrands.length} brand(s) associated with this operator. Please reassign or delete these brands first.`
        );
        return; // Block deletion
      }
    }
  
    setItemToDelete(id);
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  const handleEditBrand = async () => {
    try {
await axios.patch(
  `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/brands`,
      {
        BrandID: brandToEdit.BrandID,
        BrandName: updatedBrandName,
        OperatorID: updatedOperator.OperatorID,
        Share: parseFloat(updatedShare) / 100,
        Metadata: {
          ...brandToEdit.Metadata,
          Country: updatedBrandCountry,
          Region: updatedBrandRegion,
        }
      },
      { headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY } }
    );
      setEditBrandDialogOpen(false);
      fetchBrandsAndOperators();
    } catch (error) {
      console.error('Error updating brand:', error);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs value={selectedTab} onChange={handleTabChange} centered>
        <Tab label="Brands" />
        <Tab label="Operators" />
      </Tabs>

      {selectedTab === 0 && (
        <Box>
          <Button
            variant="contained"
            color="primary"
            sx={{ marginTop: 2, marginBottom: 2 }}
            onClick={() => setCreateBrandDialogOpen(true)}
          >
            CREATE BRAND
          </Button>
          <Box sx={{ mb: 2 }}>
            {Object.entries(shareByRegionAndCountry).map(([region, countries]) => (
              <Box key={region} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {region}
                </Typography>
                {Object.entries(countries).map(([country, total]) => (
                  <Typography key={country} variant="body2" sx={{ color: total === 100 ? 'green' : 'red' }}>
                    {country}: {total.toFixed(1)}%
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>

          <Autocomplete
            options={['All Countries', ...countryOptions]}
            value={selectedCountry}
            onChange={(e, newValue) => setSelectedCountry(newValue || 'All Countries')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Country"
                variant="outlined"
                margin="dense"
                sx={{ mb: 2, width: 300 }}
              />
            )}
          />



          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell onClick={() => toggleSort('BrandName')} style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                    Brand Name {renderSortIcon('BrandName')}
                  </TableCell>
                  <TableCell>Operator Name</TableCell>
                  <TableCell onClick={() => toggleSort('Country')} style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                    Country {renderSortIcon('Country')}
                  </TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell>Share (%)</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBrands.map((brand) => {
                  const metadata = brand.Metadata || {};
                  return (
                    <TableRow key={brand.BrandID}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{brand.BrandName}</Typography>
                          <CopyToClipboardText text={brand.BrandID} />
                        </Box>
                      </TableCell>

                      <TableCell>
                        {operators.find((op) => op.OperatorID === brand.OperatorID)?.OperatorName || 'Unknown'}
                      </TableCell>
                      <TableCell>{metadata.Country || ''}</TableCell>
                      <TableCell>{metadata.Region || ''}</TableCell>
                      <TableCell>{(parseFloat(brand.Share) * 100).toFixed(1)}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<EditIcon />}
                          sx={{ marginRight: 2 }}
                          onClick={() => {
                            setBrandToEdit(brand);
                            setUpdatedBrandName(brand.BrandName);
                            setUpdatedOperator(
                              operators.find((op) => op.OperatorID === brand.OperatorID) || null
                            );
                            setUpdatedShare((parseFloat(brand.Share) * 100).toFixed(1));
                            setUpdatedBrandCountry(metadata.Country || '');
                            setUpdatedBrandRegion(metadata.Region || '');
                            setEditBrandDialogOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<DeleteIcon />}
                          onClick={() => confirmDelete(brand.BrandID, 'brand')}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}

              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {selectedTab === 1 && (
        <Box>
          <Button
            variant="contained"
            color="primary"
            sx={{ marginTop: 2, marginBottom: 2 }}
            onClick={() => setCreateOperatorDialogOpen(true)}
          >
            CREATE OPERATOR
          </Button>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Operator Name</TableCell>
                  <TableCell>License Number</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {operators.map((operator) => (
                  <TableRow key={operator.OperatorID}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{operator.OperatorName}</Typography>
                        <CopyToClipboardText text={operator.OperatorID} />
                      </Box>
                    </TableCell>

                    <TableCell>{operator.LicenseNumber}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<DeleteIcon />}
                        onClick={() => confirmDelete(operator.OperatorID, 'operator')}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Create Brand Dialog */}
      <Dialog open={createBrandDialogOpen} onClose={() => setCreateBrandDialogOpen(false)}>
        <DialogTitle>Create Brand</DialogTitle>
        <DialogContent>
          <TextField
            label="Brand Name"
            fullWidth
            value={newBrandName}
            onChange={(e) => setNewBrandName(e.target.value)}
            margin="dense"
          />
          <Autocomplete
            options={operators}
            getOptionLabel={(option) => option.OperatorName}
            value={selectedOperator}
            onChange={(event, newValue) => setSelectedOperator(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Select Operator" variant="outlined" margin="dense" fullWidth />
            )}
          />
          <TextField
            label="Share (%)"
            fullWidth
            value={newBrandShare}
            onChange={(e) => setNewBrandShare(e.target.value)}
            margin="dense"
            type="number"
          />
          <Autocomplete
            freeSolo
            options={countryOptions}
            value={newBrandCountry}
            onChange={(e, newValue) => setNewBrandCountry(newValue)}
            onInputChange={(e, newInputValue) => setNewBrandCountry(newInputValue)}
            renderInput={(params) => (
              <TextField {...params} label="Country" variant="outlined" margin="dense" fullWidth />
            )}
          />

          <Autocomplete
            freeSolo
            options={regionOptions}
            value={newBrandRegion}
            onChange={(e, newValue) => setNewBrandRegion(newValue)}
            onInputChange={(e, newInputValue) => setNewBrandRegion(newInputValue)}
            renderInput={(params) => (
              <TextField {...params} label="Region" variant="outlined" margin="dense" fullWidth />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateBrandDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateBrand}
            disabled={!newBrandName || !selectedOperator || !newBrandShare}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editBrandDialogOpen} onClose={() => setEditBrandDialogOpen(false)}>
        <DialogTitle>Edit Brand</DialogTitle>
        <DialogContent>
          <TextField
            label="Brand Name"
            fullWidth
            value={updatedBrandName}
            onChange={(e) => setUpdatedBrandName(e.target.value)}
            margin="dense"
          />
          <Autocomplete
            options={operators}
            getOptionLabel={(option) => option.OperatorName}
            value={updatedOperator}
            onChange={(event, newValue) => setUpdatedOperator(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Select Operator" variant="outlined" margin="dense" fullWidth />
            )}
          />
          <TextField
            label="Share (%)"
            fullWidth
            value={updatedShare}
            onChange={(e) => setUpdatedShare(e.target.value)}
            margin="dense"
            type="number"
          />
          <Autocomplete
            freeSolo
            options={countryOptions}
            value={updatedBrandCountry}
            onChange={(e, newValue) => setUpdatedBrandCountry(newValue)}
            onInputChange={(e, newInputValue) => setUpdatedBrandCountry(newInputValue)}
            renderInput={(params) => (
              <TextField {...params} label="Country" variant="outlined" margin="dense" fullWidth />
            )}
          />

          <Autocomplete
            freeSolo
            options={regionOptions}
            value={updatedBrandRegion}
            onChange={(e, newValue) => setUpdatedBrandRegion(newValue)}
            onInputChange={(e, newInputValue) => setUpdatedBrandRegion(newInputValue)}
            renderInput={(params) => (
              <TextField {...params} label="Region" variant="outlined" margin="dense" fullWidth />
            )}
          />

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBrandDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleEditBrand()}
            disabled={!updatedBrandName || !updatedOperator || !updatedShare}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Operator Dialog */}
      <Dialog open={createOperatorDialogOpen} onClose={() => setCreateOperatorDialogOpen(false)}>
        <DialogTitle>Create Operator</DialogTitle>
        <DialogContent>
          <TextField
            label="Operator Name"
            fullWidth
            value={newOperatorName}
            onChange={(e) => setNewOperatorName(e.target.value)}
            margin="dense"
          />
          <TextField
            label="License Number"
            fullWidth
            value={newLicenseNumber}
            onChange={(e) => setNewLicenseNumber(e.target.value)}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOperatorDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateOperator}
            disabled={!newOperatorName || !newLicenseNumber}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {deleteType === 'brand' ? 'brand' : 'operator'}? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteItem}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandsOperatorsPage;
